import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { mealPlanAssoc } from '@/app/components/search/utils/buildDealCards/createHotelDetailDeeplink.ts';
import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
// import { getDeepLinkId } from '@/app/components/search/Janus/utils';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import { type FARE_TYPE, getAirlineId, getRouteId } from '@/app/common/starTrack/events/utils/resultClickedUtils.ts';
import { getTripIdFromDealCard } from '@/app/common/starTrack/events/utils/startrackEventsUtils.ts';
import { createTotalPriceFareType } from '@/app/common/starTrack/events/utils/eventsUtils.ts';

export const RESULT_CLICKED = 'RESULT_CLICKED';
export const RESULT_CLICKED_VERSION = '4.0';

export type ResultClickedPayload = {
  searchId: string;
  vcSearchId: string;
  internalHotelId: number;
  clickId: string;
  tripId: string;
  page: number;
  position: number;
  mealPlanClass: string;
  ctaName: string;
  currency: string;
  totalPrice: number;
  accommodationType: string;
  flashSalesFlag: boolean;
  freeCancellation: boolean;
  providerId: string;
  fareType: FARE_TYPE | null;
  strikeThroughPrice: number;
  outboundAirlineId: string | null;
  inboundAirlineId: string | null;
  outboundRouteId: string | null;
  inboundRouteId: string | null;
  totalPriceEuro: number;
  hotelLabel: string;
  strikethroughTotalPriceEuro: number;
};

export type ResultClickedParams = {
  searchParams: SearchParams;
  dealCard: DealCard;
  clickId: string;
  vcSearchId?: string;
  localization: Localization;
  ctaName: string;
};

export const createResultClickedPayload = ({
  searchParams,
  dealCard,
  clickId,
  vcSearchId,
  localization,
  ctaName,
}: ResultClickedParams): ResultClickedPayload => {
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const vcSearchIdToUse = vcSearchId || searchParams[URLParameters.VC_SEARCH_ID] || '';
  if (!searchId) throw new Error('searchId not present in searchParams');

  const currency = localization?.currency;
  const pageParam = searchParams[URLParameters.PAGE];
  const page = pageParam ? parseInt(pageParam.toString(), 10) : 1;
  const position = dealCard.index;
  const mealPlan = dealCard?.product?.rate?.features?.mealPlan;
  const mealPlanClass = mealPlanAssoc[`${mealPlan}`] || '';
  const totalPrice = dealCard.product.rate.price.total.price;
  const accommodationType = dealCard.product.accommodation.type.toUpperCase();
  const flashSalesFlag = dealCard?.product?.promotions?.flashSale || false;
  const freeCancellation = dealCard.product.rate.features.cancellation;
  const providerId = `${dealCard?.product?.accommodation?.providerId}`;
  const strikeThroughPrice = dealCard.product.rate.price.total.strikethrough;
  const strikethroughTotalPriceEuro = dealCard.product.rate.price.total.strikethroughEur ?? 0;
  const transportData = dealCard?.transport?.data;
  const transportId = dealCard?.product?.transport?.transportId;
  const totalPriceEuro = dealCard?.product?.rate?.price?.total?.priceEur;
  const tripId = getTripIdFromDealCard(dealCard);
  const { facilities } = dealCard.product.accommodation;
  const hotelLabel =
    facilities?.familyBabySitting ||
    facilities?.familyChildrenClubs ||
    facilities?.familyChildrenMeal ||
    facilities?.familyChildrenPool
      ? `Family`
      : facilities?.adultsOnly
        ? 'Couple'
        : '';
  return {
    searchId,
    vcSearchId: vcSearchIdToUse,
    internalHotelId: dealCard?.product?.accommodation?.hotelId,
    clickId,
    tripId,
    page,
    position,
    mealPlanClass,
    ctaName,
    currency,
    totalPrice,
    accommodationType,
    flashSalesFlag,
    freeCancellation,
    providerId,
    fareType: createTotalPriceFareType(!!dealCard.product?.rate?.flags?.loyalty, dealCard.promotionTheme),
    strikeThroughPrice,
    outboundAirlineId: getAirlineId(
      transportData?.find((transport) => transport.transportId === transportId)?.outbound?.segments
    ),
    inboundAirlineId: getAirlineId(
      transportData?.find((transport) => transport.transportId === transportId)?.inbound?.segments
    ),
    outboundRouteId: getRouteId(
      transportData?.find((transport) => transport.transportId === transportId)?.outbound?.segments
    ),
    inboundRouteId: getRouteId(
      transportData?.find((transport) => transport.transportId === transportId)?.inbound?.segments
    ),
    totalPriceEuro,
    hotelLabel,
    strikethroughTotalPriceEuro,
  };
};
